import { useCallback, useEffect, useState } from 'react';
import { AvailabilityPage } from '@/features/availability/AvailabilityPage';
import { CeoView } from '@/features/reports/ceo/CeoView';
import { OpsView } from '@/features/reports/ops/OpsView';
import { MarketingView } from '@/features/reports/marketing/MarketingView';
import { TargetsPage } from '@/features/settings/TargetsPage';
import { SalesPage } from '@/features/sales/SalesPage';
import { ActionsPage } from '@/features/actions/ActionsPage';
import { PoPage } from '@/features/po/PoPage';
import { VisibilityPage } from '@/features/visibility/VisibilityPage';
import { DiscountPage } from '@/features/discount/DiscountPage';
import { LoginPage } from '@/features/auth/LoginPage';
import { clearSession, loadSession, type Session } from '@/lib/auth';
import { SESSION_EXPIRED_EVENT } from '@/lib/http';
import { useRoute } from '@/lib/route';

/**
 * Auth gate. `?fixture` bypasses it so the visual-regression suite can render
 * the dashboard without a live sign-in — it pins the page to comp data anyway.
 */
const BYPASS =
  typeof window !== 'undefined' && new URLSearchParams(window.location.search).has('fixture');

export default function App() {
  const [session, setSession] = useState<Session | null>(() => loadSession());
  const [route, navigate] = useRoute();
  const onNavigate = useCallback(
    (section: string, page: string) => { navigate({ section, page }); },
    [navigate],
  );

  /**
   * The API refused the session, so return to sign-in.
   *
   * The session is read once at mount, which was fine until it expired: the
   * dead token is still a string, so the gate below kept passing and every
   * panel rendered its own 401. A reload did not clear it either — it read the
   * same token back out of sessionStorage — leaving the user on a dashboard
   * where nothing loaded and only Sign out recovered it. `http.ts` has already
   * dropped the stored copy by the time this fires.
   */
  useEffect(() => {
    const onExpired = (): void => { setSession(null); };
    window.addEventListener(SESSION_EXPIRED_EVENT, onExpired);
    return () => { window.removeEventListener(SESSION_EXPIRED_EVENT, onExpired); };
  }, []);

  const signOut = useCallback(() => {
    // Revoking is fire-and-forget; the UI must not wait on the network to
    // sign someone out.
    void clearSession();
    setSession(null);
  }, []);

  if (!session && !BYPASS) {
    return <LoginPage onSignedIn={setSession} />;
  }

  if (route.section === 'ceo') {
    return <CeoView route={route} onNavigate={onNavigate} onSignOut={signOut} />;
  }
  if (route.section === 'operations') {
    return <OpsView route={route} onNavigate={onNavigate} onSignOut={signOut} />;
  }
  if (route.section === 'marketing') {
    return <MarketingView route={route} onNavigate={onNavigate} onSignOut={signOut} />;
  }
  /**
   * Settings edits the plan the Operations "Plan & forecast" page measures
   * against, so it is a sibling of the reports rather than a page inside one.
   */
  if (route.section === 'settings') {
    return <TargetsPage route={route} onNavigate={onNavigate} onSignOut={signOut} />;
  }

  /**
   * The pages the nav has always listed and the build did not have.
   *
   * Matched on section AND page so a half-typed hash lands somewhere sensible
   * rather than on a blank screen: `#/overview` with no page falls through to
   * the Sales Dashboard, which is what Overview means.
   */
  if (route.section === 'overview') {
    if (route.page === 'actions') {
      return <ActionsPage onNavigate={onNavigate} onSignOut={signOut} />;
    }
    return <SalesPage onNavigate={onNavigate} onSignOut={signOut} />;
  }
  if (route.section === 'availability' && route.page === 'po-modules') {
    return <PoPage onNavigate={onNavigate} onSignOut={signOut} />;
  }
  if (route.section === 'visibility') {
    return <VisibilityPage onNavigate={onNavigate} onSignOut={signOut} />;
  }
  if (route.section === 'discount') {
    return <DiscountPage onNavigate={onNavigate} onSignOut={signOut} />;
  }

  if (route.section === 'availability' && route.page === 'supply-chain') {
    return <AvailabilityPage onSignOut={signOut} onNavigate={onNavigate} />;
  }

  /**
   * The four original sections are still ROUTED, just not listed in the nav -
   * an existing deep link keeps working. An unknown hash now lands on the CEO
   * view, because Availability is no longer reachable from the rail and
   * landing there would strand the user.
   */
  return <CeoView route={{ section: 'ceo', page: route.page || 'market-position' }} onNavigate={onNavigate} onSignOut={signOut} />;
}
